import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Download, Loader2, Play, Square } from "lucide-react";

import { CLIP_MAX_SIDE_S, DEFAULT_REPLAY_RATE } from "../api";
import { InfoHint } from "./InfoHint";
import { useRecordingDownloads } from "./RecordingDownloadsContext";
import type { DirectReplay } from "../useDirectReplay";

/**
 * Pick a window of recorded video around a moment and play it.
 *
 * The track is symmetric about the moment the picture was captured: drag left
 * to reach back before it, right to carry on after it. Each side reaches ten
 * minutes, but the useful settings are the first few seconds, so the scale is
 * squared — half the travel is the first quarter of the range — instead of
 * giving a 30-second clip twelve pixels of a linear track.
 */

const STEP_S = 5;
const DEFAULT_SIDE_S = 10;
/** Squared response: `f` is the fraction of one half-track from the centre. */
const CURVE = 2;
/** Major marks on each half of the track (seconds). */
const MAJOR_TICKS_S = [10, 60, 300, 600];

function fractionToSeconds(f: number): number {
  const raw = CLIP_MAX_SIDE_S * Math.pow(Math.min(1, Math.max(0, f)), CURVE);
  return Math.min(CLIP_MAX_SIDE_S, Math.round(raw / STEP_S) * STEP_S);
}

function secondsToFraction(s: number): number {
  return Math.pow(Math.min(1, Math.max(0, s / CLIP_MAX_SIDE_S)), 1 / CURVE);
}

/** "45s" / "2m 30s" — short enough to sit inline next to the track. */
function shortDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return rest ? `${m}m ${rest}s` : `${m}m`;
}

function tickLabel(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = seconds / 60;
  return Number.isInteger(m) ? `${m}m` : `${m.toFixed(1)}m`;
}

function tickPct(seconds: number, side: ThumbSide): number {
  const offset = secondsToFraction(seconds) * 50;
  return side === "pre" ? 50 - offset : 50 + offset;
}

function clockLabel(ms: number): string {
  return new Date(ms).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function PlaybackSeeker({
  fromMs,
  toMs,
  positionMs,
  onSeek,
}: {
  fromMs: number;
  toMs: number;
  positionMs: number;
  onSeek: (ms: number) => void;
}) {
  const span = Math.max(1, toMs - fromMs);
  const pct = Math.min(100, Math.max(0, ((positionMs - fromMs) / span) * 100));
  const barRef = useRef<HTMLDivElement>(null);

  function atClientX(clientX: number): number {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return positionMs;
    const f = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round(fromMs + f * span);
  }

  return (
    <div className="min-w-0 flex-1">
      <div
        ref={barRef}
        role="slider"
        aria-label="Playback position"
        aria-valuemin={fromMs}
        aria-valuemax={toMs}
        aria-valuenow={positionMs}
        aria-valuetext={clockLabel(positionMs)}
        tabIndex={0}
        className="relative h-10 cursor-pointer select-none touch-none"
        onPointerDown={(e) => {
          e.preventDefault();
          onSeek(atClientX(e.clientX));
          const move = (ev: PointerEvent) => onSeek(atClientX(ev.clientX));
          const up = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
          };
          window.addEventListener("pointermove", move);
          window.addEventListener("pointerup", up);
        }}
        onKeyDown={(e) => {
          const step = (e.shiftKey ? 5 : 1) * 1000;
          if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
            e.preventDefault();
            onSeek(Math.max(fromMs, positionMs - step));
          } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
            e.preventDefault();
            onSeek(Math.min(toMs, positionMs + step));
          } else if (e.key === "Home") {
            e.preventDefault();
            onSeek(fromMs);
          } else if (e.key === "End") {
            e.preventDefault();
            onSeek(toMs);
          }
        }}
      >
        <div className="absolute top-1/2 h-1 w-full -translate-y-1/2 rounded-full bg-white/10" />
        <div
          className="pointer-events-none absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-bf-magenta/70"
          style={{ width: `${pct}%` }}
        />
        <div
          className="pointer-events-none absolute top-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-bf-magenta bg-bf-surface"
          style={{ left: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function PlaybackError({ error }: { error: string }) {
  if (/live video is off/i.test(error)) {
    return (
      <div className="text-xs text-red-300">
        Live video is off for this store.{" "}
        <Link
          to="/settings"
          search={{ section: "stores" }}
          className="underline hover:text-red-200"
        >
          Turn it on in Settings.
        </Link>
      </div>
    );
  }
  return <div className="text-xs text-red-300">{error}</div>;
}

type ThumbSide = "pre" | "post";

function Thumb({
  side,
  seconds,
  onSeconds,
  onDragStart,
}: {
  side: ThumbSide;
  seconds: number;
  onSeconds: (next: number) => void;
  onDragStart: (side: ThumbSide) => void;
}) {
  const offset = secondsToFraction(seconds) * 50;
  const left = side === "pre" ? 50 - offset : 50 + offset;
  return (
    <button
      type="button"
      role="slider"
      aria-label={side === "pre" ? "Seconds before" : "Seconds after"}
      aria-valuemin={0}
      aria-valuemax={CLIP_MAX_SIDE_S}
      aria-valuenow={seconds}
      aria-valuetext={shortDuration(seconds)}
      onPointerDown={(e) => {
        e.preventDefault();
        onDragStart(side);
      }}
      onKeyDown={(e) => {
        const big = e.shiftKey ? 12 : 1;
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
          e.preventDefault();
          onSeconds(seconds + (side === "pre" ? STEP_S : -STEP_S) * big);
        } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
          e.preventDefault();
          onSeconds(seconds + (side === "pre" ? -STEP_S : STEP_S) * big);
        } else if (e.key === "Home") {
          e.preventDefault();
          onSeconds(side === "pre" ? CLIP_MAX_SIDE_S : 0);
        } else if (e.key === "End") {
          e.preventDefault();
          onSeconds(side === "pre" ? 0 : CLIP_MAX_SIDE_S);
        }
      }}
      className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 text-bf-magenta transition hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/60"
      style={{ left: `${left}%` }}
    >
      {side === "pre" ? (
        <ChevronLeft size={18} strokeWidth={2.5} aria-hidden />
      ) : (
        <ChevronRight size={18} strokeWidth={2.5} aria-hidden />
      )}
      <span className="sr-only">{shortDuration(seconds)}</span>
    </button>
  );
}

export function ClipScrubber({
  cameraId,
  cameraName,
  trackKey,
  startMs,
  endMs,
  snapshotUrl,
  replay,
}: {
  cameraId: string;
  cameraName?: string;
  /** Resolves the window server-side when start/end are unknown. */
  trackKey?: string;
  startMs: number;
  endMs: number;
  snapshotUrl?: string;
  replay: DirectReplay;
}) {
  const { jobs, startDownload } = useRecordingDownloads();
  const [pre, setPre] = useState(DEFAULT_SIDE_S);
  const [post, setPost] = useState(DEFAULT_SIDE_S);
  const [error, setError] = useState<string | null>(null);
  const [, setNowTick] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<ThumbSide | null>(null);

  useEffect(() => {
    setError(null);
    setPre(DEFAULT_SIDE_S);
    setPost(DEFAULT_SIDE_S);
    // Window chrome only — playback is started by the parent when the
    // moment is selected, so this must not cancel a seek already in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, trackKey, startMs, endMs]);

  const setFromPointer = useCallback((clientX: number) => {
    const side = dragRef.current;
    const rect = trackRef.current?.getBoundingClientRect();
    if (!side || !rect || rect.width === 0) return;
    const f = (clientX - rect.left) / rect.width;
    // Each thumb owns its own half of the track: dragging past the centre
    // pins it at zero rather than letting the window invert.
    const seconds = fractionToSeconds(
      side === "pre" ? (0.5 - f) * 2 : (f - 0.5) * 2,
    );
    if (side === "pre") setPre(seconds);
    else setPost(seconds);
  }, []);

  useEffect(() => {
    const move = (e: PointerEvent) => setFromPointer(e.clientX);
    const up = () => {
      dragRef.current = null;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [setFromPointer]);

  const clampSide = (v: number) =>
    Math.min(CLIP_MAX_SIDE_S, Math.max(0, Math.round(v / STEP_S) * STEP_S));

  const fromMs = startMs - pre * 1000;
  const toMs = endMs + post * 1000;
  const positionMs =
    replay.playing && replay.startedAt != null
      ? replay.startedAt + replay.mediaTimeMs()
      : startMs;

  useEffect(() => {
    if (!replay.playing) return;
    let raf = 0;
    const tick = () => {
      setNowTick((n) => n + 1);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [replay.playing]);

  function play(atMs = startMs) {
    setError(null);
    void replay.seek(Math.round(atMs));
  }

  const windowMs = Math.max(0, endMs - startMs);
  const total = pre + post + Math.round(windowMs / 1000);
  const preLeft = 50 - secondsToFraction(pre) * 50;
  const postRight = 50 + secondsToFraction(post) * 50;
  const preparingDownload = jobs.some(
    (job) =>
      job.cameraId === cameraId &&
      job.status === "preparing" &&
      job.startMs === startMs &&
      job.endMs === endMs &&
      job.preS === pre &&
      job.postS === post,
  );

  function download() {
    if (preparingDownload) return;
    setError(null);
    startDownload({
      cameraId,
      cameraName: cameraName?.trim() || cameraId,
      startMs,
      endMs,
      preS: pre,
      postS: post,
      rate: DEFAULT_REPLAY_RATE,
      thumbUrl: snapshotUrl,
    });
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => play()}
          className="bf-button w-28 shrink-0"
          title="Play this moment"
          aria-busy={replay.pending}
        >
          <span className="inline-flex items-center justify-center gap-2">
            {replay.pending ? (
              <Loader2 size={16} className="animate-spin" aria-hidden />
            ) : (
              <>
                <Play size={16} />
                Play
              </>
            )}
          </span>
        </button>
        <button
          type="button"
          onClick={() => replay.stop()}
          disabled={!replay.playing && !replay.pending}
          className="bf-button w-28 shrink-0"
          title="Stop"
        >
          <span className="inline-flex items-center justify-center gap-2">
            <Square size={12} fill="currentColor" />
            Stop
          </span>
        </button>
        <PlaybackSeeker
          fromMs={fromMs}
          toMs={Math.max(toMs, fromMs + 1000)}
          positionMs={positionMs}
          onSeek={(ms) => play(ms)}
        />
        <span className="w-20 shrink-0 whitespace-nowrap text-right text-sm tabular-nums text-bf-muted">
          {clockLabel(positionMs)}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="relative h-4 text-[11px] leading-4 text-bf-text">
            <span
              className="absolute -translate-x-1/2 whitespace-nowrap"
              style={{ left: "50%" }}
              title="When this picture was taken"
            >
              {clockLabel(startMs)}
            </span>
          </div>
          <div
            ref={trackRef}
            role="slider"
            aria-label="Clip window around this moment"
            className="relative h-10 cursor-pointer select-none touch-none"
            onPointerDown={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const f = (e.clientX - rect.left) / rect.width;
              dragRef.current = f < 0.5 ? "pre" : "post";
              setFromPointer(e.clientX);
            }}
          >
            <div className="absolute top-1/2 h-1 w-full -translate-y-1/2 rounded-full bg-white/10" />
            {(["pre", "post"] as const).flatMap((side) =>
              MAJOR_TICKS_S.map((seconds) => {
                const pct = tickPct(seconds, side);
                return (
                  <button
                    key={`${side}-${seconds}`}
                    type="button"
                    aria-label={`${side === "pre" ? "Before" : "After"} ${tickLabel(seconds)}`}
                    className="absolute top-1/2 z-[1] h-3.5 w-3 -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${pct}%` }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      if (side === "pre") setPre(clampSide(seconds));
                      else setPost(clampSide(seconds));
                    }}
                  >
                    <span className="mx-auto block h-3.5 w-px bg-white/35" />
                  </button>
                );
              }),
            )}
            <div
              className="pointer-events-none absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-bf-magenta/70"
              style={{ left: `${preLeft}%`, right: `${100 - postRight}%` }}
            />
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded bg-bf-violet" />
            <Thumb
              side="pre"
              seconds={pre}
              onSeconds={(v) => setPre(clampSide(v))}
              onDragStart={(s) => (dragRef.current = s)}
            />
            <Thumb
              side="post"
              seconds={post}
              onSeconds={(v) => setPost(clampSide(v))}
              onDragStart={(s) => (dragRef.current = s)}
            />
          </div>
          <div className="relative h-4 text-[10px] leading-4 text-bf-muted">
            {(["pre", "post"] as const).flatMap((side) =>
              MAJOR_TICKS_S.map((seconds) => {
                const pct = tickPct(seconds, side);
                return (
                  <span
                    key={`${side}-${seconds}`}
                    className="absolute whitespace-nowrap"
                    style={{
                      left: `${pct}%`,
                      transform:
                        pct < 2
                          ? undefined
                          : pct > 98
                            ? "translateX(-100%)"
                            : "translateX(-50%)",
                    }}
                  >
                    {side === "pre" ? "−" : "+"}
                    {tickLabel(seconds)}
                  </span>
                );
              }),
            )}
          </div>
        </div>

        {/* Fixed width so "20s" / "20m 55s" do not resize the track. */}
        <span className="w-20 shrink-0 whitespace-nowrap text-right text-sm tabular-nums text-bf-muted">
          {shortDuration(total)}
        </span>
        <button
          type="button"
          onClick={download}
          disabled={preparingDownload}
          className="bf-button w-40 shrink-0 whitespace-nowrap"
        >
          <span className="inline-flex items-center justify-center gap-2">
            {preparingDownload ? (
              <Loader2 size={16} className="animate-spin" aria-hidden />
            ) : (
              <>
                <Download size={16} />
                Download
              </>
            )}
          </span>
        </button>
        <InfoHint>
          The range a download will include around this moment.
        </InfoHint>
      </div>

      {(error || replay.error) && (
        <PlaybackError error={error || replay.error || ""} />
      )}
    </div>
  );
}
